import React, { memo, useCallback, useContext, useMemo, useState } from 'react'

import {
  Button,
  ClosingDropdown,
  Icon,
  ChevronDown,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSize, iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useDccBridgeAccess from 'Pods/ThreeDStudio/Hooks/useDccBridgeAccess'
import SoftwareIcon from 'Pods/ThreeDStudio/Shared/SoftwareIcon/SoftwareIcon'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import DccBridgePopover from './Components/DccBridgePopover/DccBridgePopover'
import useDccPlugins from './Hooks/useDccPlugins'

const dccLabelFallback = 'DCC'

const trailIcon = (
  <span className="dcc-bridge-cluster__chevron">
    <Icon svgComponent={ChevronDown} color="dark" size={iconSize} notSpaced />
  </span>
)

function DccBridgeButton() {
  // The SSR flag is the single source of truth for enablement: an installer on
  // the plugins API must NOT promote a software out of "Coming soon" (Unreal
  // ships an installer but stays gated), and SendToButton reads the same SSR
  // list, so promoting here would desync the two buttons.
  const { dccSoftwares: softwares } = useContext(ThreeDStudioPageContext)
  const hasDccBridgeAccess = useDccBridgeAccess()
  // Skip the plugins fetch entirely for non-subscribers: the dropdown still opens
  // so they can see the available providers, but the per-row plugin download links
  // are gated behind the paid-feature modal, so the versions are never consumed.
  const { plugins } = useDccPlugins({ enabled: hasDccBridgeAccess })
  const selectedName = useThreeDStudioStore(state => state.dccBridge.selectedSoftwareName)

  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)
  const handleClosePopover = useCallback(() => {
    setTriggerCloseDropdown(true)
  }, [])

  const hasAnyEnabled = useMemo(
    () => softwares.some(s => s.isEnabled),
    [softwares]
  )
  const selectedSoftware = useMemo(
    () => softwares.find(s => s.name === selectedName) || null,
    [softwares, selectedName]
  )

  const buttonLabel = selectedSoftware ? selectedSoftware.name : dccLabelFallback
  const leadIcon = selectedSoftware
    ? <SoftwareIcon iconName={selectedSoftware.iconName} size={iconSizeMedium} />
    : null

  const button = (
    <Button
      icon={leadIcon}
      trailIcon={trailIcon}
      type="secondary"
      size="small"
      variant={leadIcon ? 'with-icon' : undefined}
      aria-label={selectedSoftware ? `DCC Bridge (${selectedSoftware.name})` : 'DCC Bridge'}
      disabled={!hasAnyEnabled}
      customClass="dcc-bridge-cluster__dcc"
    >
      {selectedSoftware ? null : buttonLabel}
    </Button>
  )

  return (
    <ClosingDropdown
      content={(
        <DccBridgePopover
          softwares={softwares}
          plugins={plugins}
          selectedName={selectedName}
          isLocked={!hasDccBridgeAccess}
          onClosePopover={handleClosePopover}
        />
      )}
      isSimpleContent
      size="medium"
      placement="top-end"
      contentClassName="dcc-bridge-popover-wrap"
      className="dcc-bridge-cluster__dcc-wrap"
      usePortal
      triggerCloseDropdown={triggerCloseDropdown}
      setTriggerCloseDropdown={setTriggerCloseDropdown}
    >
      {button}
    </ClosingDropdown>
  )
}

export default memo(DccBridgeButton)
